/**
 * ODT — OpenDocument text (§8 Phase 3, §16).
 *
 * The model this produces is a {@link DocxModel}, and that is a deliberate
 * design decision rather than a shortcut. `DocxModel` describes paragraphs,
 * runs, tables and pictures; none of those are Word's inventions. Producing one
 * means the document viewer, outline, search, zoom and plain-text export all
 * apply to an ODT without a second rendering path — and a second rendering path
 * is exactly where "supported" quietly becomes "supported differently" (§19).
 *
 * What is *not* shared is the save path, which is ODF's own: paragraph spans in
 * `content.xml` are rewritten in place and every other entry in the package is
 * copied through in its original compressed form. Change tracking, embedded
 * objects, fonts, forms, the whole of `styles.xml` — none of it is parsed on
 * the save path, so none of it can be lost there (§13, §24).
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { hasPart, readPart, repackPackage } from '../ooxml/package.js';
import type { DocxImage, DocxModel } from '../docx/handler.js';
import type { DocxBlock, DocxParagraph } from '../docx/document-xml.js';
import {
  CONTENT_PART, ODF_MIME, checkMimetypeEntry, imageContentType, openOdf,
} from './package.js';
import type { OdfDocument } from './package.js';
import { parseStyles } from './styles.js';
import type { OdfStyleTable } from './styles.js';
import { applyOdfTextEdits, parseOdfText } from './text-xml.js';

const PREVIEW_BLOCKS = 40;

/* ------------------------------------------------------------------ *
 * reading
 * ------------------------------------------------------------------ */

interface Read {
  readonly doc: OdfDocument;
  readonly styles: OdfStyleTable;
  readonly blocks: readonly DocxBlock[];
  readonly paragraphs: readonly DocxParagraph[];
  readonly truncated: boolean;
  readonly imageParts: readonly string[];
  readonly externalRefs: readonly string[];
}

function read(bytes: Uint8Array, maxBlocks: number, signal?: AbortSignal): Read {
  const doc = openOdf(bytes);
  if (signal) throwIfAborted(signal);
  const styles = parseStyles(doc.stylesXml, doc.contentXml);
  const parsed = parseOdfText(doc.contentXml, { styles, maxBlocks });
  return { doc, styles, ...parsed };
}

function readImages(
  doc: OdfDocument, parts: readonly string[], budgetBytes: number,
): { images: Map<string, DocxImage>; skipped: string[] } {
  const images = new Map<string, DocxImage>();
  const skipped: string[] = [];
  let spent = 0;

  for (const part of new Set(parts)) {
    const contentType = imageContentType(doc, part);
    if (!contentType || !hasPart(doc.pkg, part)) { skipped.push(part); continue; }
    try {
      const bytes = readPart(doc.pkg, part);
      if (spent + bytes.length > budgetBytes) { skipped.push(part); continue; }
      spent += bytes.length;
      images.set(part, { part, bytes, contentType });
    } catch {
      skipped.push(part);
    }
  }
  return { images, skipped };
}

function countWords(paragraphs: readonly DocxParagraph[]): number {
  let total = 0;
  for (const paragraph of paragraphs) {
    const trimmed = paragraph.text.trim();
    if (trimmed) total += trimmed.split(/\s+/).length;
  }
  return total;
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const odtHandler: FormatHandler<DocxModel> = {
  id: 'odt',
  formatIds: ['odt'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const parsed = read(source.bytes, PREVIEW_BLOCKS);

    return {
      // 'pages' rather than 'text': an ODT is a laid-out document, and this is
      // the kind the shared document viewer renders (the same one DOCX uses).
      kind: 'pages',
      unitCount: parsed.truncated ? undefined : parsed.paragraphs.length,
      unitKey: 'viewer.paragraph',
      firstContent: {
        blocks: parsed.blocks.slice(0, PREVIEW_BLOCKS),
        more: parsed.truncated,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<DocxModel>> {
    throwIfAborted(ctx.signal);
    const maxBlocks = Math.max(1, Math.floor(ctx.limits.maxSpreadsheetCells / 8));
    const parsed = read(source.bytes, maxBlocks, ctx.signal);
    if (parsed.truncated) {
      throw new HandlerError(
        'error.unableToRead', `document exceeds the block budget of ${maxBlocks}`,
      );
    }

    const { images, skipped } = readImages(
      parsed.doc, parsed.imageParts, ctx.limits.maxFileBytes / 4,
    );

    const contentXml = parsed.doc.contentXml;
    const hasTrackedChanges = contentXml.includes('<text:tracked-changes')
      && contentXml.includes('<text:changed-region');
    const hasComments = contentXml.includes('<office:annotation');
    const hasFootnotes = contentXml.includes('<text:note');
    const hasHeadersOrFooters = parsed.doc.stylesXml.includes('<style:header')
      || parsed.doc.stylesXml.includes('<style:footer');
    // ODF keeps Basic macros in their own package directory, so their presence
    // is a fact about the archive rather than a guess from the content.
    const hasMacros = parsed.doc.pkg.directory.entries.some((e) =>
      e.name.startsWith('Basic/') || e.name.startsWith('Scripts/'));

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (parsed.doc.mimetype && parsed.doc.mimetype !== ODF_MIME['odt']) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: `the package declares ${parsed.doc.mimetype}, which is not an OpenDocument text document`,
      });
    }
    if (parsed.doc.stylesMissing || parsed.styles.empty) {
      warnings.push({
        messageKey: 'error.formattingMayNotBePreserved',
        severity: 'degraded',
        detail: 'styles.xml is missing or declares no styles, so text is shown unformatted. Saving preserves the part regardless.',
      });
    }
    if (hasMacros) {
      warnings.push({
        messageKey: 'security.macrosPresent',
        severity: 'caution',
        detail: 'This document carries Basic macros. They are never executed and are preserved unchanged on save (§30).',
      });
    }
    if (hasTrackedChanges) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Tracked changes are preserved on save but shown in their accepted form; paragraphs holding one cannot be text-edited.',
      });
    }
    if (hasComments) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Comments are preserved on save but not displayed; paragraphs holding one cannot be text-edited.',
      });
    }
    if (parsed.externalRefs.length > 0) {
      warnings.push({
        messageKey: 'security.externalContent',
        severity: 'caution',
        detail: `${parsed.externalRefs.length} pictures are linked from outside the file and were not fetched (§29): ${parsed.externalRefs.slice(0, 3).join(', ')}`,
      });
    }
    if (skipped.length > 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: `${skipped.length} pictures could not be decoded for display; they are preserved on save.`,
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large document (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        blocks: parsed.blocks,
        paragraphs: parsed.paragraphs,
        edits: new Map(),
        images,
        hasMacros,
        hasTrackedChanges,
        hasComments,
        hasHeadersOrFooters,
        hasFootnotes,
        wordCount: countWords(parsed.paragraphs),
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: DocxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const doc = openOdf(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    if (model.edits.size > 0) {
      // Re-parse from the original bytes so the spans belong to this XML rather
      // than to whatever the session has been doing to the model.
      const styles = parseStyles(doc.stylesXml, doc.contentXml);
      const parsed = parseOdfText(doc.contentXml, { styles, maxBlocks: Number.MAX_SAFE_INTEGER });

      const result = applyOdfTextEdits(
        doc.contentXml, parsed.paragraphs,
        [...model.edits].map(([index, text]) => ({ index, text })),
      );
      if (result.failed.length > 0) {
        throw new HandlerError(
          'error.unableToRead',
          `could not rewrite paragraphs: ${result.failed.join(', ')}`,
        );
      }
      dropped.push(...result.dropped);
      replacements.set(CONTENT_PART, new TextEncoder().encode(result.xml));
    }

    const repacked = repackPackage(doc.pkg, replacements);
    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: DocxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const parsed = read(bytes, Number.MAX_SAFE_INTEGER);
      reopened = true;
      issues.push(...checkMimetypeEntry(parsed.doc.pkg, ODF_MIME['odt']!));

      if (parsed.paragraphs.length !== model.paragraphs.length) {
        issues.push(`paragraph count changed: ${model.paragraphs.length} → ${parsed.paragraphs.length}`);
      }
      for (const [i, expected] of model.paragraphs.entries()) {
        const actual = parsed.paragraphs[i];
        if (!actual) { issues.push(`paragraph ${i} disappeared`); continue; }
        const want = model.edits.get(expected.index) ?? expected.originalText;
        if (actual.text !== want) {
          issues.push(`paragraph ${i}: ${JSON.stringify(want)} → ${JSON.stringify(actual.text)}`);
        }
        // The style name is what carries the formatting; losing it is exactly
        // the failure a rebuild-from-model save would produce.
        if (actual.styleId !== expected.styleId) {
          issues.push(`paragraph ${i} lost its style reference (${expected.styleId} → ${actual.styleId})`);
        }
        if (actual.headingLevel !== expected.headingLevel) {
          issues.push(`paragraph ${i} changed heading level`);
        }
      }
      for (const part of model.images.keys()) {
        if (!hasPart(parsed.doc.pkg, part)) issues.push(`picture part missing: ${part}`);
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported document: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing
 * ------------------------------------------------------------------ */

/** Queue a paragraph's new text. Nothing is written until export (§13). */
export function setOdtParagraphText(
  model: DocxModel, index: number, text: string,
): DocxModel {
  const paragraph = model.paragraphs.find((p) => p.index === index);
  if (!paragraph) throw new HandlerError('error.unableToRead', `no paragraph at index ${index}`);
  if (!paragraph.editable) {
    throw new HandlerError(
      'error.featuresUnsupported',
      `paragraph ${index} contains content that text editing cannot preserve`,
    );
  }

  const edits = new Map(model.edits);
  if (text === paragraph.originalText) edits.delete(index);
  else edits.set(index, text);

  const first = paragraph.runs.find((r) => r.kind === 'text');
  const updated: DocxParagraph = {
    ...paragraph,
    text,
    runs: [{
      kind: 'text',
      text,
      bold: first?.kind === 'text' ? first.bold : false,
      italic: first?.kind === 'text' ? first.italic : false,
      underline: first?.kind === 'text' ? first.underline : false,
      strike: first?.kind === 'text' ? first.strike : false,
      vertical: first?.kind === 'text' ? first.vertical : null,
      highlight: first?.kind === 'text' ? first.highlight : null,
      color: first?.kind === 'text' ? first.color : null,
      href: first?.kind === 'text' ? first.href : null,
      revision: null,
    }],
  };

  const replace = (block: DocxBlock): DocxBlock => {
    if (block.kind === 'paragraph') return block.index === index ? updated : block;
    return {
      ...block,
      rows: block.rows.map((row) => row.map((cell) => ({
        ...cell,
        paragraphs: cell.paragraphs.map((p) => (p.index === index ? updated : p)),
      }))),
    };
  };

  const paragraphs = model.paragraphs.map((p) => (p.index === index ? updated : p));
  return {
    ...model,
    edits,
    blocks: model.blocks.map(replace),
    paragraphs,
    wordCount: countWords(paragraphs),
  };
}
